import axios from 'axios';
import { showErrorToast } from '../lib/toast';
import { supabase } from '../lib/supabaseClient';

// Ensure HTTPS in production
const IS_DEV = process.env.REACT_APP_ENVIRONMENT === 'development' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

export const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // In production, force HTTPS
    if (process.env.NODE_ENV === 'production' && !envUrl.includes('localhost')) {
      return envUrl.replace(/^http:\/\//i, 'https://');
    }
    return envUrl;
  }
  // Default to localhost for development
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
const ERROR_TOAST_DEDUPE_MS = 4000;
let lastErrorToastKey = null;
let lastErrorToastAt = 0;

const getGenericErrorMessage = (error) => {
  const status = error?.response?.status;
  const apiDetail = error?.response?.data?.detail;
  const apiCode = error?.response?.data?.error;
  if (!status) {
    const code = error?.code;
    const msg = error?.message ? String(error.message) : '';
    if (code === 'ECONNABORTED' || /timeout/i.test(msg)) {
      return 'Request timed out. Please try again.';
    }
    if (code === 'ERR_NETWORK') {
      return 'Network error. Check your connection and try again.';
    }
    return 'Something went wrong. Please try again.';
  }
  if (status === 429 && apiCode === 'quota_exhausted') {
    return apiDetail || "You've reached a limit for this plan. Upgrade your plan to continue.";
  }
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 429) return 'Too many requests. Please wait and try again.';
  if (status >= 500) return 'Something went wrong. If the problem persists, please contact support.';
  return 'Something went wrong. If the problem persists, please contact support.';
};

const maybeShowGlobalErrorToast = (error) => {
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
  if (error?.config?.suppressGlobalErrorToast) return;

  const message = getGenericErrorMessage(error);
  const status = error?.response?.status || 'network';
  const method = error?.config?.method || 'unknown';
  const url = error?.config?.url || 'unknown';
  const dedupeKey = `${status}:${method}:${url}`;
  const now = Date.now();

  if (lastErrorToastKey === dedupeKey && now - lastErrorToastAt < ERROR_TOAST_DEDUPE_MS) {
    return;
  }

  lastErrorToastKey = dedupeKey;
  lastErrorToastAt = now;
  showErrorToast(message);
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in headers
apiClient.interceptors.request.use(
  async (config) => {
    let token = null;

    if (IS_DEV) {
      token = localStorage.getItem('manteo_auth_token');
    } else {
      const { data: { session } } = await supabase.auth.getSession();
      token = session?.access_token;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle unauthorized errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      // With Supabase, we might not need to manually handle 401 retries
      // for token refresh, as the request interceptor fetches the current session.
      // If we still get a 401, the user is likely truly unauthorized.
      console.error('Unauthorized request');
    }
    maybeShowGlobalErrorToast(error);
    return Promise.reject(error);
  }
);

export async function authenticatedFetch(url, options = {}) {
  let token = null;

  if (IS_DEV) {
    token = localStorage.getItem('manteo_auth_token');
  } else {
    const { data: { session } } = await supabase.auth.getSession();
    token = session?.access_token;
  }

  // Set authorization header
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok && response.status === 401) {
    console.error('Unauthorized fetch');
  }

  return response;
}

export default apiClient;
